<template>
    <v-row>
        <v-col cols="12" md="7">
            <v-img :src="product.image_url"
                   max-height="600"
                   contain
                   class="cursor-pointer"
                   @click="openLightbox(0)"
            ></v-img>
            <div class="product-image-thumbnails mt-3" v-if="imageUrls.length > 1">
                <button v-for="(imageUrl, index) in imageUrls.slice(1)"
                        :key="imageUrl"
                        type="button"
                        class="product-image-thumbnail"
                        @click="openLightbox(index + 1)"
                >
                    <v-img :src="imageUrl" width="64" height="64" contain></v-img>
                </button>
            </div>
        </v-col>

        <v-col cols="12" md="5">
            <v-card outlined class="mb-3">
                <v-card-text>
                    <template v-if="showProductDescription">
                        <p class="mb-0 font-weight-bold">{{ $t('products.description') }}</p>
                        <p class="mb-1" v-html="product.description ? product.description : `-`"></p>
                    </template>

                    <template v-if="showProductBrand">
                        <p class="mb-0 font-weight-bold">{{ $t('products.brand') }}</p>
                        <p class="mb-1">{{ product.brand }}</p>
                    </template>

                    <template v-if="showProductModel">
                        <p class="mb-0 font-weight-bold">{{ $t('products.model') }}</p>
                        <p class="mb-1">{{ product.model }}</p>
                    </template>

                    <template v-if="showProductType">
                        <v-divider class="my-2"/>
                        <p class="mb-0 font-weight-bold">{{ $t('products.product_type') }}</p>
                        <p class="mb-1">{{ product.product_type ? product.product_type : '-' }}</p>
                    </template>

                    <template v-if="showProductAttributes && product.attributes.length > 0">
                        <p class="mb-0 font-weight-bold">{{ $t('products.attributes') }}</p>
                        <p v-for="(attribute, i) in product.attributes" :key="i" class="mb-0">
                            {{ attribute.key }}: <i>{{ attribute.value }}</i>
                        </p>
                    </template>

                    <template v-if="showProductInStock">
                        <v-divider class="my-2"/>
                        <p class="mb-0 font-weight-bold">{{ $t('products.in_stock_alt') }}</p>
                        <p class="mb-1">{{ product.in_stock }} {{ $t('products.pieces') }}</p>

                        <p class="mt-3 mb-1 font-weight-bold">{{ $t('products.batches') }}</p>
                        <p v-if="batches === null" class="mb-1 grey--text text--darken-1" role="status">
                            {{ $t('products.batches_unavailable') }}
                        </p>
                        <p v-else-if="batches.length === 0" class="mb-1 grey--text text--darken-1">
                            {{ $t('products.batches_empty') }}
                        </p>
                        <template v-else>
                            <v-simple-table dense class="product-batches-table">
                                <template v-slot:default>
                                    <thead>
                                    <tr>
                                        <th scope="col" class="text-left">{{ $t('products.batch_code') }}</th>
                                        <th scope="col" class="text-left">{{ $t('products.batch_expiration') }}</th>
                                        <th scope="col" class="text-right">{{ $t('products.batch_quantity') }}</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="batch in batches"
                                        :key="batch.external_id"
                                        :class="{ 'grey--text text--darken-1': batch.free_quantity === 0 }"
                                    >
                                        <td class="product-batch-code">
                                            {{ batch.code || '–' }}
                                            <span v-if="batch.manufactured_date" class="product-batch-manufactured caption">
                                                {{ $t('products.batch_manufactured') }}: {{ formatBatchDate(batch.manufactured_date) }}
                                            </span>
                                        </td>
                                        <td class="product-batch-date">{{ formatBatchDate(batch.expire_date) }}</td>
                                        <td class="product-batch-quantity text-right">
                                            {{ batch.free_quantity }} {{ $t('products.pieces') }}
                                        </td>
                                    </tr>
                                    </tbody>
                                </template>
                            </v-simple-table>
                        </template>
                    </template>

                    <template v-if="showBuyPrice || showBuyPriceVat || showSellPrice || showSellPriceVat">
                        <v-divider class="my-2"/>
                    </template>

                    <template v-if="showBuyPrice">
                        <p class="mb-0 font-weight-bold">{{ $t('products.suggested_buy_price_without_vat') }}</p>
                        <p class="mb-1">{{ product.suggested_buy_price_without_vat|currency }}</p>
                    </template>

                    <template v-if="showBuyPriceVat">
                        <p class="mb-0 font-weight-bold">{{ $t('products.suggested_buy_price_with_vat') }}</p>
                        <p class="mb-1">{{ product.suggested_buy_price_with_vat|currency }}</p>
                    </template>

                    <template v-if="showSellPrice">
                        <p class="mb-0 font-weight-bold">{{ $t('products.suggested_sell_price_without_vat') }}</p>
                        <p class="mb-1">{{ product.suggested_sell_price_without_vat|currency }}</p>
                    </template>

                    <template v-if="showSellPriceVat">
                        <p class="mb-0 font-weight-bold">{{ $t('products.suggested_sell_price_with_vat') }}</p>
                        <p class="mb-1">{{ product.suggested_sell_price_with_vat|currency }}</p>
                    </template>
                </v-card-text>
            </v-card>

            <p class="mb-1 font-weight-bold">{{ $t('products.add_to_cart') }}</p>
            <add-to-cart-form-component :warehouse-id="warehouseId" :product="product"/>
        </v-col>

        <v-dialog v-model="lightboxOpen" max-width="1200">
            <v-card class="black">
                <v-btn class="lightbox-close" dark icon @click="lightboxOpen = false">
                    <v-icon>mdi-window-close</v-icon>
                </v-btn>
                <v-carousel v-model="lightboxIndex" hide-delimiter-background height="80vh">
                    <v-carousel-item v-for="imageUrl in imageUrls" :key="imageUrl">
                        <v-img :src="imageUrl" height="100%" contain></v-img>
                    </v-carousel-item>
                </v-carousel>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import AddToCartFormComponent from "@/components/user/products/AddToCartFormComponent";
import SnackbarMixin from "@/mixins/SnackbarMixin";
import ProductMixin from '@/mixins/ProductMixin'
import _ from 'lodash'

export default {
    name: 'ProductDetailComponent',
    mixins: [SnackbarMixin, ProductMixin],
    components: { AddToCartFormComponent },
    data: () => ({
        lightboxOpen: false,
        lightboxIndex: 0
    }),
    props: {
        warehouseId: Number,
        product: Object
    },
    filters: {
        currency: function (price) {
            return "" + _.toInteger(price).toLocaleString(undefined, { minimumFractionDigits: 2 }) + " ,-"
        }
    },
    computed: {
        batches () {
            return Array.isArray(this.product.batches) ? this.product.batches : null;
        },
        imageUrls () {
            return this.product.image_urls && this.product.image_urls.length > 0
                ? this.product.image_urls
                : [this.product.image_url];
        }
    },
    methods: {
        formatBatchDate (date) {
            if (!date) {
                return '–';
            }

            const parsed = this.$moment(date, 'YYYY-MM-DD', true);
            return parsed.isValid() ? parsed.locale(this.$i18n.locale).format('L') : '–';
        },
        openLightbox (index) {
            this.lightboxIndex = index;
            this.lightboxOpen = true;
        }
    }
}
</script>

<style scoped>
.product-batches-table th,
.product-batches-table td {
    padding: 8px 4px !important;
    vertical-align: top;
}

.product-batch-code {
    overflow-wrap: anywhere;
    word-break: break-word;
}

.product-batch-manufactured {
    display: block;
}

.product-batch-date,
.product-batch-quantity {
    white-space: nowrap;
    width: 1%;
}

.product-image-thumbnails {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.product-image-thumbnail {
    width: 72px;
    height: 72px;
    padding: 3px;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 2px;
    cursor: pointer;
    transition: border-color .15s ease, box-shadow .15s ease;
}

.product-image-thumbnail:hover,
.product-image-thumbnail:focus {
    border-color: #1976d2;
    box-shadow: 0 0 0 2px rgba(25, 118, 210, .2);
    outline: none;
}

.lightbox-close {
    position: absolute;
    right: 8px;
    top: 8px;
    z-index: 1;
}
</style>
